import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Crown, CheckCircle2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("success");
          return;
        }
        if (data.status === "expired" || attempts.current > 10) { setStatus("error"); return; }
      } catch {
        if (attempts.current > 10) { setStatus("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="payment-result">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-primary animate-spin mb-6" size={40} />
            <h1 className="font-display text-3xl mb-2">Confirming payment…</h1>
            <p className="text-muted-foreground text-sm">Hang tight while we verify with Stripe.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-secondary mb-6" size={48} />
            <h1 className="font-display text-4xl mb-3 flex items-center justify-center gap-2"><Crown className="text-primary" /> YOU'RE PREMIUM</h1>
            <p className="text-muted-foreground mb-8">All three zones, enhanced hints and the full archive are now unlocked.</p>
            <Link to="/" data-testid="payment-continue" className="inline-block bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3.5 border border-primary hover-lift">Start Playing</Link>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-display text-3xl mb-3">Hmm, something went off-beat</h1>
            <p className="text-muted-foreground mb-8">We couldn't confirm the payment. If you were charged, it'll activate shortly.</p>
            <Link to="/premium" className="inline-block border border-white/30 font-mono-caps text-xs px-6 py-3.5 hover:border-primary">Back to Premium</Link>
          </>
        )}
      </div>
    </Layout>
  );
}
